import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useVoltarAuditorias } from '@/modules/system/auditorias/use-voltar-auditorias';
import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import type { ReadUsuarioDto } from '@/api.types';
import { GetAuditoriaRelatorios } from './auditoria-relatorios.service';
import type { AuditoriaRelatorioDto, FindAuditoriaRelatoriosDto } from './auditoria-relatorios.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

function tempo(ms: number) {
  return `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
}

export default function LogsRelatoriosPage() {
  useVoltarAuditorias();
  const hoje = new Date();
  const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

  const [loaded, setLoaded] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [dataInicial, setDataInicial] = useState(primeiroDia);
  const [dataFinal, setDataFinal] = useState(ultimoDia);
  const [relatorio, setRelatorio] = useState('');
  const [idUsuario, setIdUsuario] = useState<number>(0);
  const [listaUsuarios, setListaUsuarios] = useState<ReadUsuarioDto[]>([]);
  const [registros, setRegistros] = useState<[AuditoriaRelatorioDto[], number]>();

  const tableState = useDataTableState('geradoEm');

  useEffect(() => {
    GetUsuarios({ skip: 0, take: 1000 })
      .then((result) => {
        const ordenados = result[0].sort((a, b) => (a.username ?? '').localeCompare(b.username ?? ''));
        setListaUsuarios(ordenados);
      })
      .catch((e) => toast.error(String(e)))
      .finally(() => setLoaded(true));
  }, []);

  const columns: Column<AuditoriaRelatorioDto>[] = useMemo(
    () => [
      {
        key: 'geradoEm',
        header: 'Data',
        render: (row) => format(new Date(row.geradoEm), 'dd/MM/yyyy HH:mm', { locale: ptBR }),
      },
      { key: 'usuarioNome', header: 'Quem gerou' },
      { key: 'relatorio', header: 'Relatório' },
      { key: 'filtros', header: 'Filtros' },
      {
        key: 'duracaoMs',
        header: 'Tempo',
        render: (row) => tempo(row.duracaoMs),
      },
    ],
    [],
  );

  async function handleBuscar() {
    if (!loaded) return;
    setBuscando(true);
    try {
      const filters: FindAuditoriaRelatoriosDto = {
        skip: (tableState.currentPage - 1) * tableState.pageSize,
        take: tableState.pageSize,
        data_inicial: dataInicial.toISOString(),
        data_final: dataFinal.toISOString(),
        relatorio: relatorio.trim() || undefined,
        usuario_id: idUsuario || undefined,
      };
      setRegistros(await GetAuditoriaRelatorios(filters));
    } catch (e) {
      toast.error(String(e));
    } finally {
      setBuscando(false);
    }
  }

  useEffect(() => {
    void handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, tableState.currentPage, tableState.pageSize, tableState.sortConfig]);

  return (
    <div className="row">
      <div className="col-12">
        <h3>Auditoria de Relatórios</h3>
        <span className="blockquote-footer pb-4 text-end">
          Quem gerou, o relatório, os filtros e o tempo da consulta.
        </span>
      </div>
      <hr />

      {registros && (
        <div className="col-12">
          <DataTable
            columns={columns}
            data={registros[0]}
            onChange={handleBuscar}
            serverSide={true}
            showFilter={false}
            states={tableState}
            totalRecords={registros[1]}
            renderFilters={() => (
              <div className="row">
                <div className="col-md-3">
                  <label htmlFor="usuario-relatorio" className="col-form-label">
                    Usuário:
                  </label>
                  <select
                    id="usuario-relatorio"
                    className="form-select"
                    value={idUsuario}
                    onChange={(e) => setIdUsuario(Number(e.target.value))}
                  >
                    <option value={0}>Todos</option>
                    {listaUsuarios.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-5">
                  <label htmlFor="nome-relatorio" className="col-form-label">
                    Relatório contém:
                  </label>
                  <input
                    id="nome-relatorio"
                    className="form-control"
                    value={relatorio}
                    onChange={(e) => setRelatorio(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="data-inicial-relatorio" className="col-form-label">
                    Inicial:
                  </label>
                  <input
                    id="data-inicial-relatorio"
                    type="date"
                    className="form-control"
                    value={dataInicial.toISOString().slice(0, 10)}
                    onChange={(e) => setDataInicial(new Date(`${e.target.value}T00:00:00`))}
                  />
                </div>
                <div className="col-md-2">
                  <label htmlFor="data-final-relatorio" className="col-form-label">
                    Final:
                  </label>
                  <input
                    id="data-final-relatorio"
                    type="date"
                    className="form-control"
                    value={dataFinal.toISOString().slice(0, 10)}
                    onChange={(e) => setDataFinal(new Date(`${e.target.value}T00:00:00`))}
                  />
                </div>
                <div className="col-12 mt-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={handleBuscar}
                    disabled={buscando}
                  >
                    {buscando ? 'Buscando...' : 'Pesquisar'}
                  </button>
                </div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
